/**
 * Einstellungen für die MJML-Bausteine.
 *
 * Das MJML-Plugin bringt die Komponenten mit, aber kaum bearbeitbare
 * Eigenschaften - für einen Textbaustein bietet es nur "Id" und "Titel" an.
 * Damit ließe sich keine Farbe und keine Schriftgröße ändern.
 *
 * Ein Stil-Manager wäre hier der falsche Weg: MJML nimmt Gestaltung über
 * ATTRIBUTE des Bausteins entgegen (`<mj-text color="#325186">`), nicht über
 * CSS-Klassen. Deshalb werden die Attribute hier als Einstellungsfelder
 * ergänzt - dann ändert ein Klick genau das, was der Compiler auch ausliest.
 *
 * Die Farbauswahl ist bewusst auf die Palette der Kanzlei beschränkt: so kann
 * niemand versehentlich aus der CI ausbrechen.
 */

import { TOKENS } from './ci-bausteine.js';

/** Farbauswahl aus dem Designsystem. */
const FARBEN = [
  { value: '', name: 'Standard' },
  { value: TOKENS.text, name: 'Text dunkel' },
  { value: TOKENS.textSekundaer, name: 'Text grau' },
  { value: TOKENS.primaer, name: 'Blau (Marke)' },
  { value: TOKENS.akzentDunkel, name: 'Grün (Akzent)' },
  { value: TOKENS.grund, name: 'Weiß' },
];

/** Hintergrundfarben. */
const FLAECHEN = [
  { value: '', name: 'Keine' },
  { value: TOKENS.grund, name: 'Weiß' },
  { value: TOKENS.flaeche, name: 'Mint hell' },
  { value: TOKENS.flaecheBlau, name: 'Blau hell' },
  { value: TOKENS.primaer, name: 'Blau (Marke)' },
];

const AUSRICHTUNG = [
  { value: 'left', name: 'Links' },
  { value: 'center', name: 'Mittig' },
  { value: 'right', name: 'Rechts' },
];

const STAERKE = [
  { value: '400', name: 'Normal' },
  { value: '700', name: 'Fett' },
  { value: '300', name: 'Leicht' },
];

/**
 * @param {string} name
 * @param {string} beschriftung
 * @param {{value: string, name: string}[]} optionen
 */
const auswahl = (name, beschriftung, optionen) => ({
  type: 'select',
  name,
  label: beschriftung,
  options: optionen,
});

/**
 * @param {string} name
 * @param {string} beschriftung
 * @param {string} [platzhalter]
 */
const text = (name, beschriftung, platzhalter) => ({
  type: 'text',
  name,
  label: beschriftung,
  ...(platzhalter ? { placeholder: platzhalter } : {}),
});

/**
 * Ergänzt die Einstellungen der MJML-Komponenten.
 *
 * @param {any} editor
 */
export function einstellungenErgaenzen(editor) {
  const komponenten = editor.DomComponents;

  /**
   * @param {string} typ
   * @param {any[]} felder
   */
  const erweitern = (typ, felder) => {
    const vorhanden = komponenten.getType(typ);
    if (!vorhanden) return;
    komponenten.addType(typ, {
      model: {
        defaults: {
          // Die eigenen Felder ersetzen die kargen Vorgaben des Plugins.
          traits: felder,
        },
      },
    });
  };

  erweitern('mj-text', [
    auswahl('color', 'Textfarbe', FARBEN),
    text('font-size', 'Schriftgröße', 'z. B. 16px'),
    auswahl('font-weight', 'Schriftstärke', STAERKE),
    auswahl('align', 'Ausrichtung', AUSRICHTUNG),
    text('line-height', 'Zeilenabstand', 'z. B. 1.6'),
    text('padding', 'Innenabstand', 'z. B. 10px 25px'),
  ]);

  erweitern('mj-section', [
    auswahl('background-color', 'Hintergrund', FLAECHEN),
    text('padding', 'Innenabstand', 'z. B. 20px 0'),
    auswahl('text-align', 'Ausrichtung', AUSRICHTUNG),
  ]);

  erweitern('mj-column', [
    auswahl('background-color', 'Hintergrund', FLAECHEN),
    text('width', 'Breite', 'z. B. 50%'),
    text('padding', 'Innenabstand'),
  ]);

  erweitern('mj-button', [
    text('href', 'Verweisziel', 'https://…'),
    auswahl('background-color', 'Hintergrund', FLAECHEN),
    auswahl('color', 'Textfarbe', FARBEN),
    text('font-size', 'Schriftgröße', 'z. B. 16px'),
    text('border-radius', 'Ecken', 'z. B. 3px'),
    auswahl('align', 'Ausrichtung', AUSRICHTUNG),
    text('padding', 'Innenabstand'),
  ]);

  erweitern('mj-image', [
    text('src', 'Bildadresse', 'https://…'),
    // Ohne Alt-Text bleibt bei blockierten Bildern in Outlook eine leere
    // Fläche - deshalb steht das Feld weit oben und wird benannt.
    text('alt', 'Bildbeschreibung (wichtig)', 'Was zeigt das Bild?'),
    text('width', 'Breite', 'z. B. 200px'),
    text('href', 'Verweisziel (optional)', 'https://…'),
    auswahl('align', 'Ausrichtung', AUSRICHTUNG),
    text('padding', 'Innenabstand'),
  ]);

  erweitern('mj-divider', [
    auswahl('border-color', 'Farbe', FARBEN),
    text('border-width', 'Stärke', 'z. B. 1px'),
    text('padding', 'Innenabstand'),
  ]);

  erweitern('mj-body', [
    auswahl('background-color', 'Hintergrund', FLAECHEN),
    text('width', 'Breite der Nachricht', 'z. B. 600px'),
  ]);
}
